/**
 * Per-entity condition tracker (B03).
 *
 * Owns the lifecycle described in `combat-physical.md` §"Condition system":
 *   - `apply` — install a condition, respecting the per-id stack policy.
 *   - `tick`  — advance every condition on a target by `delta` seconds,
 *               returning any tick-damage emissions for the caller to apply.
 *   - `cleanse` — bulk remove based on the trigger (rest type, restoration
 *                 spell, combat-end timer).
 *   - `clear`   — remove a single condition.
 *
 * Stat-modifier hooks (Blessed adds +1d4, Slow doubles cooldowns, etc.) are
 * out of scope for B03 — that wiring lands in B04.
 */

import type { ConditionId } from "../types/entity.js";
import {
  type ActiveCondition,
  type CleanseTrigger,
  type ConditionTickDamage,
  getConditionDef,
} from "../types/condition.js";

export interface ApplyOptions {
  /** Override the registry default duration (status conditions only). */
  readonly durationSec?: number;
  /** Override the registry default tick count (tick conditions only). */
  readonly ticks?: number;
  /** instanceId of the attacker / spellcaster / trap, if known. */
  readonly sourceId?: string | null;
}

export class ConditionManager {
  /** targetId → conditionId → active instance. */
  private readonly state = new Map<string, Map<ConditionId, ActiveCondition>>();

  // ── Apply ─────────────────────────────────────────────────────────────────

  /**
   * Install or refresh a condition on `targetId`. Returns `true` if the call
   * mutated state (a new condition was added or an existing one extended /
   * refreshed), `false` if the per-id stack policy is `ignore` and the
   * condition was already present.
   */
  apply(targetId: string, conditionId: ConditionId, opts: ApplyOptions = {}): boolean {
    const def = getConditionDef(conditionId);
    if (!def) return false;

    const bucket = this.bucketFor(targetId);
    const existing = bucket.get(conditionId);
    const sourceId = opts.sourceId ?? null;

    if (def.kind === "status") {
      const duration = opts.durationSec ?? def.defaultDurationSec;
      if (!existing) {
        bucket.set(conditionId, {
          id: conditionId,
          kind: "status",
          remainingSec: duration,
          sourceId,
        });
        return true;
      }
      if (existing.kind !== "status") return false;
      switch (def.stack) {
        case "refresh":
          existing.remainingSec = duration;
          return true;
        case "extend":
          existing.remainingSec += duration;
          return true;
        case "ignore":
          return false;
      }
    }

    // tick branch
    const ticks = opts.ticks ?? def.defaultTicks;
    if (!existing) {
      bucket.set(conditionId, {
        id: conditionId,
        kind: "tick",
        remainingTicks: ticks,
        timeUntilNextTickSec: def.tickIntervalSec,
        sourceId,
      });
      return true;
    }
    if (existing.kind !== "tick") return false;
    switch (def.stack) {
      case "refresh":
        existing.remainingTicks = ticks;
        existing.timeUntilNextTickSec = def.tickIntervalSec;
        return true;
      case "extend":
        existing.remainingTicks += ticks;
        return true;
      case "ignore":
        return false;
    }
  }

  // ── Tick ──────────────────────────────────────────────────────────────────

  /**
   * Advance every condition on `targetId` by `delta` seconds.
   *   - Status conditions whose `remainingSec` reaches 0 are removed.
   *   - Tick conditions fire one tick (returned in the result) each time
   *     `timeUntilNextTickSec` reaches 0; the budget rolls over so a large
   *     `delta` can fire multiple ticks. The condition is removed when its
   *     `remainingTicks` hits 0.
   */
  tick(targetId: string, delta: number): readonly ConditionTickDamage[] {
    const bucket = this.state.get(targetId);
    if (!bucket || bucket.size === 0) return [];

    const emissions: ConditionTickDamage[] = [];

    for (const cond of [...bucket.values()]) {
      if (cond.kind === "status") {
        cond.remainingSec -= delta;
        if (cond.remainingSec <= 0) bucket.delete(cond.id);
        continue;
      }

      const def = getConditionDef(cond.id);
      if (!def || def.kind !== "tick") {
        bucket.delete(cond.id);
        continue;
      }

      let budget = delta;
      while (budget > 0 && cond.remainingTicks > 0) {
        if (budget < cond.timeUntilNextTickSec) {
          cond.timeUntilNextTickSec -= budget;
          budget = 0;
          break;
        }
        budget -= cond.timeUntilNextTickSec;
        cond.timeUntilNextTickSec = def.tickIntervalSec;
        cond.remainingTicks -= 1;
        emissions.push({
          targetId,
          conditionId: cond.id,
          damageType: def.tickDamageType,
          amount: def.tickDamageAmount,
        });
      }

      if (cond.remainingTicks <= 0) bucket.delete(cond.id);
    }

    if (bucket.size === 0) this.state.delete(targetId);
    return emissions;
  }

  // ── Cleanse / clear ───────────────────────────────────────────────────────

  /**
   * Remove every condition on `targetId` whose def lists `trigger` in
   * `cleanseOn`. Returns the ids that were cleansed.
   */
  cleanse(targetId: string, trigger: CleanseTrigger): readonly ConditionId[] {
    const bucket = this.state.get(targetId);
    if (!bucket) return [];
    const removed: ConditionId[] = [];
    for (const [id, cond] of bucket) {
      const def = getConditionDef(cond.id);
      if (def && def.cleanseOn.includes(trigger)) {
        bucket.delete(id);
        removed.push(id);
      }
    }
    if (bucket.size === 0) this.state.delete(targetId);
    return removed;
  }

  /** Remove a single condition. No-op when not present. */
  clear(targetId: string, conditionId: ConditionId): boolean {
    const bucket = this.state.get(targetId);
    if (!bucket) return false;
    const removed = bucket.delete(conditionId);
    if (removed && bucket.size === 0) this.state.delete(targetId);
    return removed;
  }

  /** Drop every condition tracked for `targetId` (e.g. on death). */
  clearAll(targetId: string): void {
    this.state.delete(targetId);
  }

  // ── Queries ───────────────────────────────────────────────────────────────

  has(targetId: string, conditionId: ConditionId): boolean {
    return this.state.get(targetId)?.has(conditionId) ?? false;
  }

  get(targetId: string, conditionId: ConditionId): ActiveCondition | null {
    return this.state.get(targetId)?.get(conditionId) ?? null;
  }

  list(targetId: string): readonly ActiveCondition[] {
    const bucket = this.state.get(targetId);
    return bucket ? [...bucket.values()] : [];
  }

  /** Drop every tracked condition for every entity. */
  reset(): void {
    this.state.clear();
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private bucketFor(targetId: string): Map<ConditionId, ActiveCondition> {
    let bucket = this.state.get(targetId);
    if (!bucket) {
      bucket = new Map();
      this.state.set(targetId, bucket);
    }
    return bucket;
  }
}

/** Singleton shared by combat / spell systems. */
export const conditionManager = new ConditionManager();
